/**
 * Dell reply codes — the authoritative NSC pair list, and a resolver for the several shapes the
 * value actually arrives in.
 *
 * NSC supplied 16 `display / Dell` pairs (e.g. `CNL3 / S03`) and specified that **the display half
 * is what the UI shows**. Both vocabularies are written into the same `DellReplyCode` field —
 * `S03` from the PUDO day-5 and SIP-return flows, `DEL` from the 19:00 updater — so anything that
 * groups or displays raw values double-counts (a cancellation appears once as `CNL3` and again as
 * `S03`). Everything reading a reply code must go through {@link resolveDellReplyCode}.
 *
 * ## Known conflicts with the Cegeka WOT integration spec
 *
 * The SmartTeams App's own outbound `ticket_event` actions write `wm_order.u_replycode` values that
 * do not line up with this list (Cegeka "Work Order Task System Integration", pp. 14–16):
 *
 * | Action                          | Spec writes            | This list says            |
 * | ------------------------------- | ---------------------- | ------------------------- |
 * | `customer_requested_reschedule` | `C01`, labelled CAD    | CAD is **C03**; C01 unknown |
 * | `attempted_contact`             | `CNA [C04]`            | CNA is **C02**; C04 is **CUS** |
 * | `unsuccessful_visit`            | `CFS - Just Go [E09]`  | not in the 16 pairs       |
 * | `cancellation_requested`        | `CCL – … [CCL]`        | not in the 16 pairs       |
 * | `access_not_granted`            | `C05`                  | ✓ CRA                     |
 * | `other_problem`                 | `TPM [E10]`            | ✓ TPM                     |
 *
 * `C04` is the dangerous one: it resolves to a *valid but different* pair, so a ticket the app
 * meant as "Customer Not Available" would read as "Customer Other Reason" — a silent mislabel
 * rather than an obvious gap. The resolver therefore never guesses: an unrecognised value is
 * returned as-is with no meaning attached, so it shows up as unknown instead of as the wrong thing.
 *
 * ## Confirmed against live data
 *
 * ServiceNow began returning the code on 2026-09-18. Of 2000 open tickets on nsctest only two
 * carried one, and they bear the above out:
 *
 * | `value` | `display_value`                      | Resolves to                     |
 * | ------- | ------------------------------------ | ------------------------------- |
 * | `P05`   | `PND - Parts not dispatched [P05]`   | **PND** — Part not dispatched   |
 * | `CCL`   | `CCL - Cancellation Requested [CCL]` | unmapped — CCL is in neither column |
 *
 * So `CCL` is real, in production data, and outside the 16 pairs — a 13th unmapped code on top of
 * the 12 the validator already accepts. It is deliberately *not* added to
 * {@link UNMAPPED_REPLY_CODES}, which has a specific provenance (the disabled validator's list);
 * this one comes from the Cegeka write path instead.
 */

export interface DellReplyCode {
  /** The half NSC wants displayed, e.g. `CNL3`. */
  display: string;
  /** Dell's own code, e.g. `S03`. */
  dell: string;
  meaning: string;
}

/** The 16 authoritative pairs, as supplied by NSC. */
export const DELL_REPLY_CODES: readonly DellReplyCode[] = [
  { display: 'CAD', dell: 'C03', meaning: 'Customer Arranged Date' },
  { display: 'CDN', dell: 'C21', meaning: 'Customer Damage Part Replaced' },
  { display: 'CDR', dell: 'C22', meaning: 'Customer Damage Part Not Replaced' },
  { display: 'CNA', dell: 'C02', meaning: 'Customer Not Available' },
  { display: 'CNL1', dell: 'S01', meaning: 'Service Cancelled with Engineer Onsite' },
  { display: 'CNL3', dell: 'S03', meaning: 'Service Cancelled before Engineer Onsite' },
  { display: 'CRA', dell: 'C05', meaning: 'Customer Refused Access' },
  { display: 'CUS', dell: 'C04', meaning: 'Customer Other Reason' },
  { display: 'DEL', dell: 'D20', meaning: 'Other Dell exception' },
  { display: 'ENA', dell: 'E11', meaning: 'Engineer Not Available' },
  { display: 'EXT', dell: 'X02', meaning: 'Other external reason' },
  { display: 'INI', dell: 'D02', meaning: 'Insufficient information' },
  { display: 'NCA', dell: 'X01', meaning: 'Non-Coverage Area' },
  { display: 'PNA', dell: 'P02', meaning: 'Parts Not Available' },
  { display: 'PND', dell: 'P05', meaning: 'Part not dispatched' },
  { display: 'TPM', dell: 'E10', meaning: 'Other Partner Reason' },
];

const BY_DISPLAY = new Map(DELL_REPLY_CODES.map(c => [c.display, c] as const));
const BY_DELL = new Map(DELL_REPLY_CODES.map(c => [c.dell, c] as const));

/**
 * Codes the (currently disabled) validator accepts that appear in neither NSC column.
 *
 * Kept as data rather than dropped so they can be recognised as "known-unmapped" instead of being
 * mistaken for corrupt values, and so the list is in one place while ServiceNow's allowed-value
 * list is being checked against the 16 pairs.
 */
export const UNMAPPED_REPLY_CODES: readonly string[] = [
  'BAD',
  'C10',
  'D01',
  'DOA',
  'E12',
  'E20',
  'INC',
  'LHO',
  'P17',
  'P18',
  'WPB',
  'NotSet',
];

const UNMAPPED = new Set(UNMAPPED_REPLY_CODES.map(c => c.toUpperCase()));

export interface ResolvedReplyCode {
  /** What to show. The display half when the value is recognised, otherwise the raw value. */
  label: string;
  meaning?: string;
  /** The matched pair, absent when the value isn't one of the 16. */
  pair?: DellReplyCode;
  /** True when the value is a known-but-unmapped code rather than an unrecognised one. */
  knownUnmapped: boolean;
}

/**
 * Resolve whatever is stored in `DellReplyCode` to the display half of its pair.
 *
 * Accepts every shape seen in the wild, because the writers disagree:
 *   - a display code — `CNL3`
 *   - a Dell code — `S03`, `C01`
 *   - `DISPLAY [DELL]` — `CNA [C04]`, `TPM [E10]`
 *   - `DISPLAY - Label [DELL]` — `CFS - Just Go [E09]`, `CCL – Cancellation Requested [CCL]`
 *
 * For the bracketed forms the bracketed Dell code is authoritative and the prefix is ignored: the
 * prefix is exactly what is wrong in the `CNA [C04]` case, so trusting it would preserve the
 * mislabel instead of surfacing it.
 *
 * Returns `null` for blank input. Never throws, and never maps an unrecognised value onto a pair.
 */
export function resolveDellReplyCode(raw: string | null | undefined): ResolvedReplyCode | null {
  const value = (raw ?? '').trim();
  if (!value) return null;

  // `DISPLAY [DELL]` / `DISPLAY - Label [DELL]` — take what's in the brackets.
  const bracketed = /\[([^\]]+)\]\s*$/.exec(value);
  const candidates = bracketed?.[1] ? [bracketed[1].trim()] : [];
  // Otherwise the whole value, or its first token for `CODE - Label` without brackets.
  candidates.push(value, value.split(/[\s–-]/)[0]?.trim() ?? value);

  for (const candidate of candidates) {
    if (!candidate) continue;
    const key = candidate.toUpperCase();
    const pair = BY_DISPLAY.get(key) ?? BY_DELL.get(key);
    if (pair) return { label: pair.display, meaning: pair.meaning, pair, knownUnmapped: false };
  }

  const first = candidates.find(Boolean) ?? value;
  return { label: value, knownUnmapped: UNMAPPED.has(first.toUpperCase()) };
}
